import * as XLSX from 'xlsx';

export const parseInventoryFile = (arrayBuffer) => {
    const workbook = XLSX.read(arrayBuffer);
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }); // Read as array of arrays

    console.log("Total rows in file:", jsonData.length);

    // 1. Find the header row
    let headerRowIndex = -1;
    let headerMap = {};

    // Helper to normalize string for comparison (removes special chars, spaces, keeps Hebrew/English)
    const normalize = (str) => String(str).toLowerCase().replace(/[^a-z0-9\u0590-\u05FF]/g, '');

    // Search Logic
    for (let i = 0; i < Math.min(jsonData.length, 25); i++) {
        const row = jsonData[i];
        if (!row) continue;

        const rowStr = row.map(cell => normalize(cell)).join('');

        // Strict check: Row must contain BOTH "sku"/"makat" AND "stock"/"mlai"
        // This avoids false positives in metadata rows
        const hasSKU = rowStr.includes(normalize('מקט')) || rowStr.includes('sku');
        const hasStock = rowStr.includes(normalize('מלאי'));

        if (hasSKU && hasStock) {
            headerRowIndex = i;
            console.log("Found confirmed header at row:", i, row);
            break;
        }
    }

    // Fallback if not found by strict check: Look for just SKU in Row 7
    if (headerRowIndex === -1 && jsonData.length > 6) {
        const row = jsonData[6];
        if (row) {
            const rowStr = row.map(cell => normalize(cell)).join('');
            if (rowStr.includes(normalize('מקט')) || rowStr.includes('sku')) {
                headerRowIndex = 6;
                console.log("Fallback: Using Row 7 as header based on SKU presence");
            }
        }
    }

    if (headerRowIndex === -1) {
        console.error("Critical: Could not find header row (looked for SKU + Stock). First 5 rows:", jsonData.slice(0, 5));
        throw new Error("לא נמצאה שורת כותרת תקינה (חפשנו שורה עם 'מק\"ט' ו-'מלאי').");
    }

    // Build Header Map
    const headerRow = jsonData[headerRowIndex];
    headerRow.forEach((cell, index) => {
        if (cell) {
            const cleanKey = normalize(cell); // Use normalized keys for mapping
            headerMap[cleanKey] = index;
            // Also store original trimmed string for debug
            headerMap[String(cell).trim()] = index;
        }
    });

    console.log("Header Map Built (Normalized):", headerMap);

    // 2. Parse data rows
    const parsedItems = [];

    // Value Getter using normalized keys
    const getValue = (row, potentialNames) => {
        if (!Array.isArray(potentialNames)) potentialNames = [potentialNames];

        for (const name of potentialNames) {
            const normName = normalize(name);

            // Try normalized match
            const exactIndex = headerMap[normName];
            if (exactIndex !== undefined) return row[exactIndex];

            // Try loose match in original keys (if map holds un-normalized keys too)
            const fuzzyKey = Object.keys(headerMap).find(k => k.includes(name));
            if (fuzzyKey) return row[headerMap[fuzzyKey]];
        }
        return undefined;
    };

    for (let i = headerRowIndex + 1; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row || row.length === 0) continue;

        // SKU
        const sku = getValue(row, ['מקט יצרן', 'מק"ט', 'SKU']);
        if (!sku) continue;

        const description = getValue(row, ['תיאור', 'שם מוצר', 'Description']) || `Item ${sku}`;

        // Stock
        const physicalStock = Number(getValue(row, ['מלאי', 'Physical'])) || 0;

        // On Order ("נמל")
        const onOrder = Number(getValue(row, ['נמל', 'OnOrder'])) || 0;

        // Velocity
        const monthlySales = Number(getValue(row, ['מכירות חודש נוכחי', 'מכירות', 'Velocity'])) || 0;
        const avgDailySales = monthlySales / 30;

        // Allocated
        const allocatedCustomer = Number(getValue(row, ['בית לקוח'])) || 0;
        const allocatedBranch = Number(getValue(row, ['סניף'])) || 0;
        const allocated = allocatedCustomer + allocatedBranch;

        // Classification
        let classification = getValue(row, ['סיווג', 'Class']);
        if (!classification) {
            classification = monthlySales > 50 ? 'A' : monthlySales > 10 ? 'B' : 'C';
        }

        parsedItems.push({
            SKU: String(sku),
            Description: String(description),
            PhysicalStock: physicalStock,
            OnOrder: onOrder,
            Backorders: 0,
            Allocated: allocated,
            AvgDailySales: avgDailySales,
            Velocity: monthlySales,
            Classification: String(classification).toUpperCase()
        });
    }

    console.log(`Parsed ${parsedItems.length} valid items.`);
    return parsedItems;
};
